import React, { useMemo, useRef, useState } from 'react';
import usStates from '../../../../../jsonData/usStates.json';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Formik, Form } from 'formik';
import * as yup from 'yup';

import { AccessTime, Bolt, Check, CheckCircle, Email, Phone, Security } from '@mui/icons-material';

import { InsuranceQuoteFormValues } from '../../types';
import { CreditApplication, PublicAccount, Task, TrackingEventEnum, TrackingEventsPublicType } from '@trnsact/trnsact-shared-types';
import { useTrackingEvents } from '../../../../../hooks/useTrackingEvents';
import { GaigAvailableAsset, GaigProgramCode } from '@trnsact/aftermarket-vendor-sdk-gaig';
import { SOUTH_STATES } from '../../constants';
import { getTermArrayByStep } from '../../lib';
import GAIG_LOGO from "../../../../../assets/images/aftermarket-vendor-logos/gaig-logo-wide.png";

interface Props {
  uniqueAssets: GaigAvailableAsset[];
  uniqueProgramCodes: GaigProgramCode[];
  creditApplication?: CreditApplication;
  publicAccount?: PublicAccount;
  task: Task;
  onSubmit: (values: InsuranceQuoteFormValues) => void;
}

type Step = 1 | 2;

export const InsuranceQuoteForm = ({
  uniqueAssets,
  uniqueProgramCodes,
  creditApplication,
  publicAccount,
  task,
  onSubmit,
}: Props) => {
  const classes = useStyles();

  const [availablePrograms, setAvailablePrograms] = useState<GaigProgramCode[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<GaigProgramCode | undefined>(undefined);
  const [step, setStep] = useState<Step>(1);

  const { createTrackingEvent } = useTrackingEvents();
  const hasTrackedNextClick = useRef(false);

  // IMPORTANT:
  // - We want Formik to consider prefilled values "valid" immediately without requiring user edits.
  // - This means: validateOnMount + initialValues computed from creditApplication + enableReinitialize.
  const initialValues: InsuranceQuoteFormValues = useMemo(
    () => ({
      state: creditApplication?.state ?? '',
      postalCode: creditApplication?.postalCode ?? '',
      availableAsset: '',
      selectedProgram: '',
      contractTerm: '',
      coverageTerm: '',
      billingFrequency: '',
    }),
    [creditApplication?.state, creditApplication?.postalCode],
  );

  const fullValidationSchema = yup.object({
    state: yup.string().required('Required'),
    postalCode: yup.string().required('Required'),
    availableAsset: yup.string().required('Required'),
    selectedProgram: yup.string().required('Required'),
    contractTerm: yup.string().required('Required'),
    coverageTerm: yup.string().required('Required'),
    billingFrequency: yup.string().required('Required'),
  });

  const step1ValidationSchema = yup.object({
    state: yup.string().required('Required'),
    postalCode: yup.string().required('Required'),
    availableAsset: yup.string().required('Required'),
  });

  const filterPrograms = (availableAsset: string, state: string) => {
    const filtered: GaigProgramCode[] = [];

    uniqueProgramCodes?.forEach((program) => {
      program.availableAssets.forEach((asset) => {
        if (program.ProgramCode.includes('ACVS') && !SOUTH_STATES.includes(state)) return;
        if (program.ProgramCode.includes('ACVN') && SOUTH_STATES.includes(state)) return;
        if (asset.code === availableAsset) filtered.push(program);
      });
    });

    setAvailablePrograms(filtered);
    return filtered;
  };

  const syncSelectedProgram = (programCode: string) => {
    const found = availablePrograms.find((p) => p.ProgramCode === programCode);
    setSelectedProgram(found);
    return found;
  };

  const resetStep2Fields = (setFieldValue: (field: string, value: any) => void) => {
    setAvailablePrograms([]);
    setSelectedProgram(undefined);

    setFieldValue('selectedProgram', '');
    setFieldValue('contractTerm', '');
    setFieldValue('coverageTerm', '');
    setFieldValue('billingFrequency', '');
  };

  const applicantName =
    creditApplication?.businessName ||
    `${creditApplication?.firstName ?? ''} ${creditApplication?.lastName ?? ''}`.trim() ||
    '—';

  const dealerName = publicAccount?.name ?? '—';

  const equipmentLabel =
    creditApplication?.equipmentDescription ??
    (`${creditApplication?.equipments?.[0]?.year ?? ''} ${creditApplication?.equipments?.[0]?.make ?? ''} ${creditApplication?.equipments?.[0]?.model ?? ''}`.trim() ||
    '—');

  const amountFinanced = creditApplication?.amountRequested;

  const coverageWhy = [
    'Satisfies lender insurance requirements',
    'Affordable coverage designed for your budget',
    'Protects leased, financed, or cash-purchased equipment',
  ];

  return (
    <Box className={classes.page}>
      {/* Top status */}
      <Box className={classes.topStatus}>
        <Box className={classes.statusIcon}>
          <Check className={classes.statusCheck} />
        </Box>
        <Typography variant="h4" className={classes.statusTitle}>
          Your credit application has been submitted
        </Typography>
        <Typography variant="body2" className={classes.statusSubtitle}>
          You&apos;re almost done.
        </Typography>
      </Box>

      {/* HERO OUTER CARD */}
      <Card className={classes.heroOuter} variant="outlined">
        <CardContent className={classes.heroOuterContent}>
          {/* Top strip */}
          <Box className={classes.heroTopStrip}>
            <Box className={classes.heroTopLeft}>
              <Box className={classes.heroShield}>
                <Security className={classes.heroShieldIcon} />
              </Box>
              <Typography className={classes.heroTopText}>
                You requested an insurance quote for your equipment. To generate your quote, we just need a few
                additional details.
              </Typography>
            </Box>

            <Chip
              size="small"
              icon={<AccessTime />}
              label="This takes less than 2 minutes."
              className={classes.timeGreenChip}
              sx={{
                ml: 'auto',                 // right align
                bgcolor: '#EAF7EE',         // green background
                color: '#1B5E20',
                border: '1px solid #BFE6C8',
                fontWeight: 700,
                borderRadius: '999px',
                '& .MuiChip-icon': { color: '#1B5E20' },
              }}
              variant="filled"
            />
          </Box>

          <Grid container className={classes.heroColumns}>
            <Grid item xs={12} md={6} className={classes.heroLeftCell}>

              <Box className={classes.grayBox}>
                <Box className={classes.grayInner}>
                  <Typography className={classes.sectionHeaderCentered}>Physical Damage Insurance</Typography>
                  <img src={GAIG_LOGO} alt="GAIG Logo" className={classes.logo} />
                  <Typography className={classes.insurerNameCentered}>Great American Insurance Group</Typography>

                  <Box className={classes.contactLine}>
                    <Phone className={classes.contactIcon} />
                    <Typography variant="body2" className={classes.contactText}>
                      877-878-03 80
                    </Typography>
                  </Box>
                  <Box className={classes.contactLine}>
                    <Email className={classes.contactIcon} />
                    <Typography variant="body2" className={classes.contactText}>
                      AgentDesk@gaig.com
                    </Typography>
                  </Box>

                  <Box className={classes.contactLine}>
                    <AccessTime className={classes.contactIcon} />
                    <Typography variant="body2" className={classes.contactText}>
                      Monday–Friday, 8:00 AM – 5:30 PM EST
                    </Typography>
                  </Box>

                  <Box className={classes.lightningRow}>
                    <Bolt className={classes.boltIcon} />
                    <Typography variant="body2" className={classes.lightningText}>
                      Once completed, your insurance quote will appear instantly
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} className={classes.heroRightCell}>
              <Typography className={classes.sectionHeaderCentered}>Why This Coverage Matters</Typography>

              <Stack gap={1.25} className={classes.whyStack}>
                {coverageWhy.map((text) => (
                  <Box key={text} className={classes.whyBox}>
                    <CheckCircle className={classes.whyCheck} />
                    <Typography variant="body2" className={classes.whyText}>
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* We already have your information */}
      <Card className={classes.infoCard} variant="outlined">
        <CardContent className={classes.infoContent}>
          <Typography className={classes.infoTitle}>We already have your information</Typography>

          <Grid container spacing={2} className={classes.infoGrid}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" className={classes.labelMuted}>
                Applicant Name
              </Typography>
              <Typography className={classes.valueText}>{applicantName}</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" className={classes.labelMuted}>
                Dealer
              </Typography>
              <Typography className={classes.valueText}>{dealerName}</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" className={classes.labelMuted}>
                Equipment
              </Typography>
              <Typography className={classes.valueText}>{equipmentLabel}</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" className={classes.labelMuted}>
                Amount Financed
              </Typography>
              <Typography className={classes.valueText}>
                {typeof amountFinanced === 'number' ? `$${amountFinanced.toLocaleString()}` : '—'}
              </Typography>
            </Grid>
          </Grid>

          <Box className={classes.infoLine}>
            <AccessTime className={classes.infoLineIcon} />
            <Typography variant="body2" className={classes.infoLineText}>
              This information comes from your credit application and will be used to generate your insurance quote.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Details needed */}
      <Formik
        initialValues={initialValues}
        validationSchema={step === 1 ? step1ValidationSchema : fullValidationSchema}
        validateOnMount
        enableReinitialize
        // Key trick: when creditApplication arrives async and initialValues reinitialize,
        // Formik will validate-on-mount again, so isValid reflects prefilled values.
        onSubmit={onSubmit}
      >
        {(formik) => {
          const { values, handleChange, errors, touched, dirty, setFieldValue, validateForm, isValid } = formik;

          // Make "Next" enabled as soon as required fields have values (even if not "touched"/"dirty").
          // This matches your requirement: data is already in inputs → Next should work.
          const step1Ready = Boolean(values.state && values.postalCode && values.availableAsset);

          const goNext = async () => {
            // Mark touched so errors show if something is missing
            formik.setTouched({ state: true, postalCode: true, availableAsset: true } as any, true);

            const errs = await validateForm();
            const hasStep1Errors = Boolean(errs.state || errs.postalCode || errs.availableAsset);
            if (hasStep1Errors) return;

            if (!hasTrackedNextClick.current) {
              hasTrackedNextClick.current = true;
              createTrackingEvent({
                variables: {
                  input: {
                    eventType: TrackingEventsPublicType.InsuranceExpress,
                    event: TrackingEventEnum.InsuranceQuoteFormNextClicked,
                    eventData: { creditApplicationId: creditApplication?.id, taskId: task.taskId },
                    accountId: publicAccount?.id,
                  },
                },
              });
            }

            filterPrograms(values.availableAsset, values.state);
            setStep(2);
          };

          const goBack = () => setStep(1);

          return (
            <Form>
              <Card className={classes.detailsCard} variant="outlined">
                <CardContent>
                  <Box className={classes.detailsHeader}>
                    <Typography className={classes.detailsTitle}>
                      Details needed to complete your quote
                    </Typography>

                    <Chip
                      size="small"
                      icon={<CheckCircle />}
                      label="These details are required to generate your insurance quote"
                      variant="filled"
                      sx={{
                        ml: 'auto',                 // right align
                        bgcolor: '#EAF7EE',         // green background
                        color: '#1B5E20',
                        border: '1px solid #BFE6C8',
                        fontWeight: 700,
                        borderRadius: '999px',
                        '& .MuiChip-icon': { color: '#1B5E20' },
                      }}
                    />
                  </Box>

                  <Divider className={classes.detailsDivider} sx={{ paddingTop: 4 }} />

                  {step === 1 && (
                    <>
                      {/* Row 1: Inputs only */}
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} className={classes.detailsFieldsRow}>
                        <FormControl fullWidth required size="small">
                          <InputLabel>State</InputLabel>
                          <Select
                            required
                            name="state"
                            value={values.state}
                            onChange={(event: SelectChangeEvent) => {
                              handleChange(event);
                              resetStep2Fields(setFieldValue);
                              setStep(1);
                            }}
                            label="State"
                            error={touched.state && Boolean(errors.state)}
                          >
                            {usStates.map((option: { label: string; value: string }) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          required
                          fullWidth
                          name="postalCode"
                          size="small"
                          label="Postal Code"
                          onChange={handleChange}
                          value={values.postalCode}
                          error={touched.postalCode && Boolean(errors.postalCode)}
                        />

                        <FormControl fullWidth required size="small">
                          <InputLabel>Equipment Type</InputLabel>
                          <Select
                            required
                            name="availableAsset"
                            value={values.availableAsset}
                            onChange={(event: SelectChangeEvent) => {
                              handleChange(event);
                              resetStep2Fields(setFieldValue);
                              setStep(1);
                            }}
                            label="Equipment Type"
                            error={touched.availableAsset && Boolean(errors.availableAsset)}
                          >
                            {uniqueAssets.map((asset) => (
                              <MenuItem key={asset.code} value={asset.code}>
                                {asset.displayName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>

                      {/* Row 2: Next button (separate row, right aligned) */}
                      <Box className={classes.step1ButtonRow}>
                        <Button
                          variant="contained"
                          onClick={goNext}
                          type="button"
                          disabled={!step1Ready} // ✅ enabled when values exist, not when user edits
                        >
                          Next
                        </Button>
                      </Box>
                    </>
                  )}

                  {/* STEP 2 */}
                  {step === 2 && (
                    <>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth required size="small">
                            <InputLabel>Program / Deductible</InputLabel>
                            <Select
                              required
                              name="selectedProgram"
                              value={values.selectedProgram}
                              onChange={(event: SelectChangeEvent) => {
                                handleChange(event);

                                const nextProgramCode = event.target.value as string;

                                // Reset dependent fields when program changes
                                setFieldValue('contractTerm', '');
                                setFieldValue('coverageTerm', '');
                                setFieldValue('billingFrequency', '');

                                syncSelectedProgram(nextProgramCode);
                              }}
                              label="Program / Deductible"
                              error={touched.selectedProgram && Boolean(errors.selectedProgram)}
                            >
                              {availablePrograms.map((program) => (
                                <MenuItem key={program.ProgramCode} value={program.ProgramCode}>
                                  {program.ProgramDescriptionExternal}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth required size="small" disabled={!selectedProgram}>
                            <InputLabel>Financing Term</InputLabel>
                            <Select
                              required
                              name="contractTerm"
                              value={values.contractTerm}
                              onChange={handleChange}
                              label="Financing Term"
                              error={touched.contractTerm && Boolean(errors.contractTerm)}
                            >
                              {(selectedProgram
                                ? getTermArrayByStep(selectedProgram.ContractTermMin, selectedProgram.ContractTermMax)
                                : []
                              ).map((t) => (
                                <MenuItem key={t.value} value={t.value}>
                                  {t.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth required size="small" disabled={!selectedProgram}>
                            <InputLabel>Coverage Term</InputLabel>
                            <Select
                              required
                              name="coverageTerm"
                              value={values.coverageTerm}
                              onChange={handleChange}
                              label="Coverage Term"
                              error={touched.coverageTerm && Boolean(errors.coverageTerm)}
                            >
                              {(selectedProgram
                                ? getTermArrayByStep(selectedProgram.CoverageTermMin, selectedProgram.CoverageTermMax)
                                : []
                              ).map((t) => (
                                <MenuItem key={t.value} value={t.value}>
                                  {t.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth required size="small" disabled={!selectedProgram}>
                            <InputLabel>Billing Frequency</InputLabel>
                            <Select
                              required
                              name="billingFrequency"
                              value={values.billingFrequency}
                              onChange={handleChange}
                              label="Billing Frequency"
                              error={touched.billingFrequency && Boolean(errors.billingFrequency)}
                            >
                              {(selectedProgram?.BillingFrequencyOptions ?? []).map((bf: string) => (
                                <MenuItem key={bf} value={bf}>
                                  {bf}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Box className={classes.step2Actions}>
                        <Button variant="outlined" onClick={goBack} type="button">
                          Back
                        </Button>
                        <Button variant="contained" type="submit" disabled={!isValid || !dirty}>
                          Submit
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  page: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '28px 16px 40px',
  },

  topStatus: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    gap: 6,
    marginBottom: 18,
  },
  statusIcon: {
    width: 44,
    height: 44,
    borderRadius: 999,
    backgroundColor: '#2E7D32',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusCheck: { color: '#fff' },
  statusTitle: { fontWeight: 700, textAlign: 'center' },
  statusSubtitle: { color: 'rgba(0,0,0,0.6)', textAlign: 'center' },

  heroOuter: { borderRadius: 10, marginBottom: 16, overflow: 'hidden' },
  heroOuterContent: { padding: 0 },
  heroTopStrip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    padding: '14px 16px',
    backgroundColor: '#F6F8FB',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    flexWrap: 'wrap',
  },
  heroTopLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 260,
    flex: 1,
  },
  heroShield: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: '#E7EFFB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
  },
  heroShieldIcon: { color: '#2563EB', fontSize: 20 },
  heroTopText: { color: 'rgba(0,0,0,0.72)', lineHeight: 1.35 },

  greenChip: {
    backgroundColor: '#EAF7EE',
    color: '#1B5E20',
    border: '1px solid #BFE6C8',
    fontWeight: 700,
    borderRadius: 999,
  },

  heroColumns: { margin: 0 },
  heroLeftCell: {
    padding: 16,
    borderRight: '1px solid rgba(0,0,0,0.08)',
    '@media (max-width: 899px)': { borderRight: 'none', borderBottom: '1px solid rgba(0,0,0,0.08)' },
  },
  heroRightCell: { padding: 16 },

  sectionHeaderCentered: { fontWeight: 700, marginBottom: 10, textAlign: 'center' },

  grayBox: { width: '100%', display: 'flex', justifyContent: 'center' },
  grayInner: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#F7F7F8',
    borderRadius: 10,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  // logoStub: { width: 120, height: 56, borderRadius: 8, backgroundColor: 'rgba(0,0,0,0.10)' },
  logo: { width: "90%" },
  insurerNameCentered: { fontWeight: 700, textAlign: 'center' },

  contactLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    flexWrap: 'wrap',
    textAlign: 'center',
  },
  contactIcon: { fontSize: 16, color: 'rgba(0,0,0,0.55)' },
  contactText: { color: 'rgba(0,0,0,0.68)' },
  contactDot: { width: 4, height: 4, borderRadius: 999, backgroundColor: 'rgba(0,0,0,0.35)', margin: '0 4px' },

  lightningRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 4 },
  boltIcon: { color: '#1976D2', fontSize: 18 },
  lightningText: { color: '#1976D2', fontWeight: 600, textAlign: 'center' },

  whyStack: { marginTop: 6 },
  whyBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 10,
    backgroundColor: '#EAF2FF',
    border: '1px solid #D6E6FF',
  },
  whyCheck: { color: '#2563EB', fontSize: 20, flex: '0 0 auto' },
  whyText: { color: 'rgba(0,0,0,0.74)' },

  infoCard: { borderRadius: 10, marginBottom: 16 },
  infoContent: { paddingTop: 14 },
  infoTitle: { fontWeight: 700, marginBottom: 10 },
  infoGrid: { marginTop: 0 },
  labelMuted: { color: 'rgba(0,0,0,0.55)' },
  valueText: { fontWeight: 500 },

  infoLine: { marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 },
  infoLineIcon: { color: '#1976D2', fontSize: 18 },
  infoLineText: { color: 'rgba(0,0,0,0.72)' },

  detailsCard: { borderRadius: 10 },
  detailsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  detailsTitle: {
    fontWeight: 700,
    lineHeight: 1.2,
  },
  detailsGreenChip: {
    marginLeft: 'auto',
    backgroundColor: '#EAF7EE',
    color: '#1B5E20',
    border: '1px solid #BFE6C8',
    fontWeight: 700,
    borderRadius: 999,
  },
  detailsDivider: { marginTop: 12, marginBottom: 14 },
  detailsFieldsRow: { alignItems: 'flex-end' },

  timeGreenChip: {
    backgroundColor: '#EAF7EE',
    color: '#1B5E20',
    border: '1px solid #BFE6C8',
    fontWeight: 700,
    borderRadius: 999,
  },

  step1ButtonRow: {
    marginTop: 12,
    display: 'flex',
    justifyContent: 'flex-end',
    '@media (max-width: 899px)': { justifyContent: 'center' },
  },

  step2Row: { alignItems: 'flex-end' },
  step2Actions: { marginTop: 16, display: 'flex', gap: 12, justifyContent: 'center' },
}));